import React, { useState, useEffect } from 'react';
import { api, endpoints } from '../../config/api';
import { Button, Alert } from '../ui';
import useOverlayClose from '../../hooks/useOverlayClose';
import { useT, apiErrorMessage } from '../../i18n';
import { setupMessages, failureKey } from './setupMessages';
import './SetupWizardModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MANIFEST_URL = `${API_BASE_URL}/outlook-addin/manifest-validated.xml`;
const ADMIN_CENTER_URL = 'https://admin.microsoft.com/Adminportal/Home#/Settings/IntegratedApps';

const dismissKey = (orgId) => `setup_wizard_dismissed_${orgId}`;

const messages = {
  ...setupMessages,
  markFailed: { tr: 'İşaretlenemedi, lütfen tekrar deneyin', en: 'Couldn’t mark as done, please try again' },
  stepProvider: { tr: 'DİZİN SAĞLAYICISI', en: 'DIRECTORY PROVIDER' },
  whichSystem: { tr: 'Hangi e-posta sisteminizi kullanıyorsunuz?', en: 'Which email system do you use?' },
  whichSystemText: {
    tr: 'İmzaların otomatik dağıtılabilmesi için organizasyonunuzun kullandığı e-posta sistemini bağlamanız gerekir.',
    en: 'To distribute signatures automatically, connect the email system your organization uses.',
  },
  later: { tr: 'Kurulumu daha sonra yapacağım', en: 'I’ll set this up later' },
  soon: { tr: 'Yakında', en: 'Coming soon' },
  connectGoogleTitle: { tr: 'Google Workspace\'e Bağlanın', en: 'Connect Google Workspace' },
  connectMsTitle: { tr: 'Microsoft 365\'e Bağlanın', en: 'Connect Microsoft 365' },
  gmailSoonHeading: { tr: 'Gmail Add-on Dağıtımı — Yakında', en: 'Gmail Add-on Deployment — Coming Soon' },
  deployTitle: { tr: 'Outlook Add-in\'i Dağıtın', en: 'Deploy the Outlook Add-in' },
  deployText: {
    tr: () => <>Microsoft 365 bağlantınız kuruldu ✓ Son adım: organizasyonunuzun <strong>Global Admin</strong> veya<strong> Exchange Admin</strong> yetkisine sahip bir yöneticisinin, eklentiyi Microsoft 365 Admin Center üzerinden organizasyon geneline dağıtması gerekiyor. Yaklaşık 5 dakika sürer.</>,
    en: () => <>Your Microsoft 365 connection is set up ✓ Last step: a <strong>Global Admin</strong> or <strong>Exchange Admin</strong> in your organization deploys the add-in organization-wide from the Microsoft 365 admin center. It takes about 5 minutes.</>,
  },
  step1: {
    tr: ({ url }) => <><a href={url} target="_blank" rel="noopener noreferrer">Admin Center &gt; Tümleşik Uygulamalar</a>{' '}sayfasını açın.</>,
    en: ({ url }) => <>Open{' '}<a href={url} target="_blank" rel="noopener noreferrer">Admin center &gt; Integrated apps</a>.</>,
  },
  step2: {
    tr: () => <><strong>+ Özel uygulamalar &gt; Özel uygulama yükle &gt; URL'den yükle</strong> seçin ve aşağıdaki adresi yapıştırın:</>,
    en: () => <>Choose <strong>Upload custom apps &gt; Provide link to manifest file</strong> and paste the address below:</>,
  },
  step3: {
    tr: () => <>Kullanıcı seçiminde <strong>Tüm kuruluş</strong>'u işaretleyip <strong>Dağıt</strong> deyin.</>,
    en: () => <>Under users, select <strong>Entire organization</strong> and click <strong>Deploy</strong>.</>,
  },
};

const GoogleGIcon = () => (
  <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

// `msStatus`/`googleStatus` (from GET graph|google/status/) and
// `onStatusChange` (a combined refetch trigger) are owned by Dashboard,
// which also uses them to decide whether to keep showing the persistent
// connection/Add-in cards — sharing one fetch keeps both in sync the
// moment a step completes.
export const SetupWizardModal = ({ organizationId, msResultParam, googleResultParam, msStatus, googleStatus, onStatusChange }) => {
  const [dismissed, setDismissed] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState('');
  const { t } = useT(messages);

  useEffect(() => {
    if (organizationId && localStorage.getItem(dismissKey(organizationId))) {
      setDismissed(true);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!msResultParam) return;
    if (msResultParam.connected === '0') {
      setError(t(failureKey(msResultParam.reason, 'microsoft')));
    }
  }, [msResultParam, t]);

  useEffect(() => {
    if (!googleResultParam) return;
    if (googleResultParam.connected === '0') {
      setError(t(failureKey(googleResultParam.reason, 'google')));
    }
  }, [googleResultParam, t]);

  const handleConnect = async (provider) => {
    setConnecting(true);
    setError('');
    try {
      const endpoint = provider === 'google'
        ? endpoints.organizations.googleConsentUrl
        : endpoints.organizations.graphConsentUrl;
      const res = await api.get(endpoint);
      window.location.href = res.consent_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.startFailed));
      setConnecting(false);
    }
  };

  const handleMarkDeployed = async () => {
    setMarking(true);
    setError('');
    try {
      await api.post(endpoints.organizations.addinMarkDeployed);
      await onStatusChange();
    } catch {
      setError(t('markFailed'));
    } finally {
      setMarking(false);
    }
  };

  const handleSkip = () => {
    if (organizationId) localStorage.setItem(dismissKey(organizationId), '1');
    setDismissed(true);
  };

  const overlayClose = useOverlayClose(handleSkip);

  if (dismissed || !msStatus || !googleStatus) return null;

  const msConnected = msStatus.connected;
  const googleConnected = googleStatus.connected;
  const connectedProvider = msConnected ? 'microsoft' : googleConnected ? 'google' : null;

  // Fully set up on whichever provider is connected — nothing to show.
  if (msConnected && msStatus.addin_deployed) return null;
  if (googleConnected && googleStatus.addon_deployed) return null;
  // Neither connected yet and the admin hasn't picked one in this session.
  if (!connectedProvider && !selectedProvider) {
    return (
      <div className="setup-wizard-overlay" {...overlayClose}>
        <div className="setup-wizard-modal">
          <div className="setup-wizard-stepper">
            <div className="setup-wizard-step-node active">
              <span className="setup-wizard-step-circle">1</span>
              <span className="setup-wizard-step-label">{t('stepProvider')}</span>
            </div>
            <div className="setup-wizard-step-connector" />
            <div className="setup-wizard-step-node">
              <span className="setup-wizard-step-circle">2</span>
              <span className="setup-wizard-step-label">ADD-IN</span>
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <div className="setup-wizard-body">
            <h2>{t('whichSystem')}</h2>
            <p>{t('whichSystemText')}</p>
            <div className="setup-wizard-provider-choice">
              <button type="button" className="setup-wizard-provider-btn" onClick={() => setSelectedProvider('microsoft')}>
                <svg width="36" height="36" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="11" height="11" fill="#f25022" />
                  <rect x="12" width="11" height="11" fill="#7fba00" />
                  <rect y="12" width="11" height="11" fill="#00a4ef" />
                  <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
                </svg>
                <span>Microsoft 365</span>
              </button>
              {/* Launch is Microsoft 365 only: the Gmail add-on isn't validated
                  on a real Workspace tenant yet. */}
              <button type="button" className="setup-wizard-provider-btn" disabled aria-disabled="true">
                <GoogleGIcon />
                <span>Google Workspace</span>
                <span className="setup-wizard-soon">{t('soon')}</span>
              </button>
            </div>
          </div>

          <button className="setup-wizard-skip" onClick={handleSkip}>
            {t('later')}
          </button>
        </div>
      </div>
    );
  }

  const provider = connectedProvider || selectedProvider;
  const activeStep = connectedProvider ? 2 : 1;

  return (
    <div className="setup-wizard-overlay" {...overlayClose}>
      <div className="setup-wizard-modal">
        <div className="setup-wizard-stepper">
          <div className={`setup-wizard-step-node ${activeStep === 1 ? 'active' : 'completed'}`}>
            <span className="setup-wizard-step-circle">{activeStep > 1 ? '✓' : '1'}</span>
            <span className="setup-wizard-step-label">{provider === 'google' ? 'GOOGLE WORKSPACE' : 'MICROSOFT 365'}</span>
          </div>
          <div className={`setup-wizard-step-connector ${activeStep > 1 ? 'completed' : ''}`} />
          <div className={`setup-wizard-step-node ${activeStep === 2 ? 'active' : ''}`}>
            <span className="setup-wizard-step-circle">2</span>
            <span className="setup-wizard-step-label">ADD-IN</span>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {activeStep === 1 ? (
          <div className="setup-wizard-body">
            <div className="setup-wizard-icon">
              {provider === 'google' ? <GoogleGIcon /> : (
                <svg width="36" height="36" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="11" height="11" fill="#f25022" />
                  <rect x="12" width="11" height="11" fill="#7fba00" />
                  <rect y="12" width="11" height="11" fill="#00a4ef" />
                  <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
                </svg>
              )}
            </div>
            {provider === 'google' ? (
              <>
                <h2>{t('connectGoogleTitle')}</h2>
                <p>{t('googleIntro')}</p>
              </>
            ) : (
              <>
                <h2>{t('connectMsTitle')}</h2>
                <p>{t('msIntro')}</p>
              </>
            )}
            <Button onClick={() => handleConnect(provider)} disabled={connecting}>
              {connecting ? t('redirecting') : provider === 'google' ? t('connectGoogle') : t('connectMs')}
            </Button>
          </div>
        ) : provider === 'google' ? (
          <div className="setup-wizard-body">
            <div className="setup-wizard-icon">📨</div>
            <h2>{t('gmailSoonHeading')}</h2>
            <p>{t('gmailSoonText')}</p>
          </div>
        ) : (
          <div className="setup-wizard-body">
            <div className="setup-wizard-icon">📨</div>
            <h2>{t('deployTitle')}</h2>
            <p>{t('deployText')}</p>

            <ol className="setup-wizard-steps-list">
              <li>{t('step1', { url: ADMIN_CENTER_URL })}</li>
              <li>{t('step2')}</li>
            </ol>

            <div className="setup-wizard-manifest-box">
              <code>{MANIFEST_URL}</code>
              <Button
                variant="secondary"
                onClick={() => navigator.clipboard?.writeText(MANIFEST_URL)}
              >
                {t('copy')}
              </Button>
            </div>

            <ol className="setup-wizard-steps-list" start="3">
              <li>{t('step3')}</li>
            </ol>

            <Button onClick={handleMarkDeployed} disabled={marking}>
              {marking ? t('saving') : t('stepsDone')}
            </Button>
          </div>
        )}

        <button className="setup-wizard-skip" onClick={handleSkip}>
          {t('later')}
        </button>
      </div>
    </div>
  );
};

export default SetupWizardModal;
